// Phase 2 permission matrix — frontend side. Mirrors requirement/Phase2.md.
//
//   rolePermissions   — which sidebar nav keys each role can see
//   actionPermissions — which CRUD actions each role can perform per resource
//
// Both are read-only access-control config, NOT user data. All user/member
// data is fetched from the API (see src/api.js).
//
// Scope qualifiers ("own sport" / "own team" / "self") are enforced server-
// side via userTeamScope() in server/src/auth.js. The maps below only gate
// visibility / button rendering.

export const rolePermissions = {
  ADMIN:           ["dashboard","messages","splits","members","groups","teams","schedule","availability","squad","practice","rankings","announcements","finance","media","admin"],
  PRESIDENT:       ["dashboard","messages","splits","members","groups","teams","schedule","availability","squad","practice","rankings","announcements","finance","media","admin"],
  VICE_PRESIDENT:  ["dashboard","messages","splits","members","groups","teams","schedule","availability","squad","practice","rankings","announcements","media"],
  // Phase 5 executive-committee roles. Every committee member sees the
  // League section; no other tabs by default (they may also carry
  // player/captain roles which broaden their access via role stacking).
  GENERAL_SECRETARY:     ["dashboard","messages","splits","teams","schedule","announcements","media"],
  TREASURER:             ["dashboard","messages","splits","teams","schedule","announcements","media"],
  EXEC_COMMITTEE_MEMBER: ["dashboard","messages","splits","teams","schedule","announcements","media"],
  FINANCE_HEAD:    ["dashboard","messages","splits","members","groups","finance","announcements","media"],
  CRICKET_HEAD:    ["dashboard","messages","splits","members","teams","schedule","availability","squad","practice","rankings","announcements","media"],
  VOLLEYBALL_HEAD: ["dashboard","messages","splits","members","teams","schedule","availability","squad","announcements","media"],
  // Captains still need the cross-match availability matrix to plan lineups.
  CAPTAIN:         ["dashboard","messages","splits","teams","schedule","availability","squad","practice","rankings","announcements","media"],
  // Players RSVP directly on Schedule cards — no dedicated Availability page.
  PLAYER:          ["dashboard","messages","splits","teams","schedule","squad","practice","rankings","announcements","media"]
};

// resource → action ("create" | "update" | "delete") → allowed role list.
// "read" is implicitly granted to anyone who sees the nav item.
export const actionPermissions = {
  members: {
    create: ["ADMIN","PRESIDENT"],
    update: ["ADMIN","PRESIDENT","VICE_PRESIDENT"],
    delete: ["ADMIN","PRESIDENT"]
  },
  groups: {
    create: ["ADMIN","PRESIDENT","VICE_PRESIDENT"],
    update: ["ADMIN","PRESIDENT","VICE_PRESIDENT"],
    delete: ["ADMIN","PRESIDENT","VICE_PRESIDENT"]
  },
  teams: {
    create: ["ADMIN","PRESIDENT","CRICKET_HEAD","VOLLEYBALL_HEAD"],
    update: ["ADMIN","PRESIDENT","VICE_PRESIDENT","CRICKET_HEAD","VOLLEYBALL_HEAD","CAPTAIN"],
    delete: ["ADMIN","PRESIDENT","CRICKET_HEAD","VOLLEYBALL_HEAD"]
  },
  schedule: {
    create: ["ADMIN","PRESIDENT","CRICKET_HEAD","VOLLEYBALL_HEAD","CAPTAIN"],
    update: ["ADMIN","PRESIDENT","VICE_PRESIDENT","CRICKET_HEAD","VOLLEYBALL_HEAD","CAPTAIN"],
    delete: ["ADMIN","PRESIDENT","CRICKET_HEAD","VOLLEYBALL_HEAD","CAPTAIN"],
    upload: ["ADMIN","PRESIDENT","CRICKET_HEAD","VOLLEYBALL_HEAD"]
  },
  availability: {
    create: ["ADMIN","PRESIDENT","CRICKET_HEAD","VOLLEYBALL_HEAD","CAPTAIN","PLAYER"],
    update: ["ADMIN","PRESIDENT","CRICKET_HEAD","VOLLEYBALL_HEAD","CAPTAIN","PLAYER"],
    delete: ["ADMIN","PRESIDENT","CRICKET_HEAD","VOLLEYBALL_HEAD","CAPTAIN"]
  },
  squad: {
    // §11.14 — only the captain (or admin) picks and publishes the squad.
    create: ["ADMIN","CAPTAIN"],
    update: ["ADMIN","CAPTAIN"],
    delete: ["ADMIN","CAPTAIN"]
  },
  announcements: {
    create: ["ADMIN","PRESIDENT","VICE_PRESIDENT","FINANCE_HEAD","CRICKET_HEAD","VOLLEYBALL_HEAD","CAPTAIN"],
    update: ["ADMIN","PRESIDENT","VICE_PRESIDENT","FINANCE_HEAD","CRICKET_HEAD","VOLLEYBALL_HEAD","CAPTAIN"],
    delete: ["ADMIN","PRESIDENT","VICE_PRESIDENT","FINANCE_HEAD","CRICKET_HEAD","VOLLEYBALL_HEAD","CAPTAIN"]
  },
  finance: {
    create: ["ADMIN","FINANCE_HEAD"],
    update: ["ADMIN","FINANCE_HEAD"],
    delete: ["ADMIN","FINANCE_HEAD"]
  },
  notifications: {
    create: ["ADMIN","PRESIDENT","VICE_PRESIDENT","FINANCE_HEAD","CRICKET_HEAD","VOLLEYBALL_HEAD","CAPTAIN"]
  },
  // Phase 3 — cricket player career stats. Anyone can view; only
  // leadership + captains can edit. Mirrors the server route gate.
  playerStats: {
    update: ["ADMIN","PRESIDENT","VICE_PRESIDENT","CRICKET_HEAD","CAPTAIN"]
  },
  // Phase 3 — admin-managed custom cricket stat field definitions.
  // Defining new stat fields is admin-only; mirrors server route gate.
  cricketStatFields: {
    create: ["ADMIN","PRESIDENT"],
    update: ["ADMIN","PRESIDENT"],
    delete: ["ADMIN","PRESIDENT"]
  },
  // Phase 3 — per-team flyer designer. Admin-only.
  flyerTemplates: {
    update: ["ADMIN","PRESIDENT"]
  },
  // Phase 4 — per-match cricket performance. Admin / leadership /
  // captains record per-player match results that auto-roll into
  // career stats.
  matchPerformance: {
    update: ["ADMIN","PRESIDENT","VICE_PRESIDENT","CRICKET_HEAD","CAPTAIN"]
  }
};

export function canDo(user, resource, action) {
  const allowed = actionPermissions[resource]?.[action] || [];
  return user?.roles?.some(r => allowed.includes(r)) || false;
}

// Workspace access — which of the three shells each role can enter.
// Gabruz Universe is broadly accessible (any user with at least one
// nav-visible role); the League workspace is committee-tier only; the
// Umpire workspace is UMPIRE-role only.
const LEAGUE_WORKSPACE_ROLES = [
  "ADMIN", "PRESIDENT", "VICE_PRESIDENT",
  "GENERAL_SECRETARY", "TREASURER", "EXEC_COMMITTEE_MEMBER"
];
export function canEnterWorkspace(user, workspace) {
  if (!user) return false;
  if (workspace === "gabruz")
    // Users with only the UMPIRE role have no nav in Gabruz Universe;
    // block them so they don't land on an empty shell.
    return (user.roles || []).some(r => (rolePermissions[r] || []).length > 0);
  if (workspace === "league")
    return (user.roles || []).some(r => LEAGUE_WORKSPACE_ROLES.includes(r));
  if (workspace === "umpire")
    return (user.roles || []).some(r => r === "UMPIRE");
  return false;
}
export function availableWorkspaces(user) {
  return ["gabruz", "league", "umpire"].filter(w => canEnterWorkspace(user, w));
}

// §10 — Cricket-specific player skills. Categorised so the UI can colour-code
// tags by what they contribute to the team (batting / bowling / all-round).
export const CRICKET_SKILLS = [
  { code: "OPENING_BAT",      label: "Opening Batsman",      short: "Opener",       category: "BAT"       },
  { code: "TOP_ORDER_BAT",    label: "Top-Order Batsman",    short: "Top Order",    category: "BAT"       },
  { code: "MIDDLE_ORDER_BAT", label: "Middle-Order Batsman", short: "Middle Order", category: "BAT"       },
  { code: "OFF_SPIN",         label: "Off-spin Bowler",      short: "Off-spin",     category: "BOWL_SPIN" },
  { code: "LEG_SPIN",         label: "Leg-spin Bowler",      short: "Leg-spin",     category: "BOWL_SPIN" },
  { code: "MEDIUM_PACE",      label: "Medium Pace",          short: "Medium Pace",  category: "BOWL_PACE" },
  { code: "PACE",             label: "Pace Bowler",          short: "Pace",         category: "BOWL_PACE" },
  { code: "ALL_ROUNDER",      label: "All-Rounder",          short: "All-Rounder",  category: "ALL"       },
  { code: "WICKET_KEEPER",    label: "Wicket Keeper",        short: "Wkt Keeper",   category: "WK"        }
];
export const SKILL_BY_CODE = Object.fromEntries(CRICKET_SKILLS.map(s => [s.code, s]));
